import { Controller } from "@hotwired/stimulus";
import AirDatepicker from "air-datepicker";
import localeJa from "air-datepicker/locale/ja";
import { findHoliday, isSaturday, isSunday, parseDate, toIsoDate } from "./holiday-calendar.js";

// 日付入力欄をカレンダー UI (Air Datepicker) に差し替え、日本の祝日を見分けられるようにする。
// カレンダー内で祝日・日曜は赤、土曜は青で表示し、祝日のマスには祝日名を併記する。
//
// 使い方:
//   <%= f.date_field :estimated_transfer_date, class: 'form-control',
//         data: { controller: 'holiday-datepicker', action: 'change->holiday-datepicker#sync' } %>
//
// ビューを type="date" のままにしているのは、JS が読み込まれなかった場合でも
// ブラウザ標準の日付ピッカーが基本機能として残るようにするため。
// 接続できた時点で text に変えるのは、標準ピッカーと Air Datepicker が二重に開くのを避けるため。
// 送信値は type="date" と同じ ISO 形式 (yyyy-MM-dd) のままなので、サーバー側の受け取りは変更不要。
//
// 日付の解釈・祝日判定は holiday-calendar.js に切り出してある。

// 組み込みの today ボタンは表示月を今月へ動かすだけで、日付を選ばない。
// 入力欄に置くボタンとしては「今日にする」と読まれるため、選択まで行うものに差し替える。
const TODAY_BUTTON = {
  content: (datepicker) => datepicker.locale.today,
  onClick: (datepicker) => datepicker.selectDate(new Date()),
};

class HolidayDatepickerController extends Controller {
  // AirDatepicker のインスタンス。null は「未生成」を表し、connect / disconnect が対で生成・破棄する。
  // JS 実行中にしか意味を持たない widget のハンドルのため、static values には置かない
  datepicker = null;

  connect() {
    // 標準の日付ピッカーと Air Datepicker のカレンダーが二重に開くのを避けるため text にする。
    // 残り2つは text にしたことで必要になる補い。
    // type="date" のままなら書式は標準 UI が示すし、オートコンプリートの候補も出ない。
    this.element.type = "text";
    this.element.placeholder = "YYYY-MM-DD";
    this.element.autocomplete = "off";

    // Air Datepicker は入力欄の既存値を読まないため、初期選択日として明示的に渡す
    const initialDate = parseDate(this.element.value);
    this.datepicker = new AirDatepicker(this.element, {
      locale: localeJa,
      dateFormat: "yyyy-MM-dd",
      autoClose: true,
      buttons: [TODAY_BUTTON, "clear"],
      selectedDates: initialDate ? [initialDate] : false,
      startDate: initialDate ?? new Date(),
      onRenderCell: (params) => this.#renderCell(params),
    });
  }

  // connect で生成したカレンダーを破棄する。
  // 残したままだと、要素が DOM から外れた後も body 直下のカレンダーと
  // その document リスナが残り続ける。
  disconnect() {
    this.datepicker?.destroy();
    this.datepicker = null;
  }

  // 入力欄の値に合わせてカレンダーの選択状態を更新する (入力欄 → カレンダーの一方向)。
  // 手入力の確定のほか、「予定をコピー」ボタンのように JS が値を書き換えた場合もここを通る。
  sync() {
    if (!this.datepicker) return;

    // カレンダーの選択日を入力欄の表記に直したもの。未選択なら空文字。
    const selectedDate = this.datepicker.selectedDates[0] ?? null;
    const calendarValue = selectedDate ? toIsoDate(selectedDate) : "";

    // 入力欄がこれと一致していれば同期済み。
    // Air Datepicker は選択が変わるたびに入力欄へ値を書き、そのとき change を発火する。
    // つまりこのメソッドは、自分が呼んだ selectDate / clear によって改めて呼ばれる。
    // この判定がその連鎖を止めている。
    const value = this.element.value;
    if (value === calendarValue) return;

    const date = parseDate(value);
    if (date) {
      this.datepicker.selectDate(date);
    } else if (value.trim() === "") {
      this.datepicker.clear();
    }
    // 日付として読めない文字列のときは何もしない。
    // 入力途中の可能性があり、ここで選択を解除すると打ちかけの文字まで消えてしまうため。
  }

  // カレンダーの1マスを描画する (Air Datepicker のコールバック)。
  // 日のマス以外 (月・年の一覧) は既定の描画に任せる。
  #renderCell({ date, cellType }) {
    if (cellType !== "day") return undefined;

    const holiday = findHoliday(date);
    const classes = [];
    if (holiday) classes.push("-holiday-");
    if (isSunday(date)) classes.push("-sunday-");
    if (isSaturday(date)) classes.push("-saturday-");

    if (!holiday) {
      return { classes: classes.join(" ") };
    }

    // マス幅に収まらない祝日名は CSS で省略表示されるため、title 属性で全文を補う
    return {
      classes: classes.join(" "),
      html: `${date.getDate()}<span class="holiday-datepicker-cell-name">${holiday.name}</span>`,
      attrs: { title: holiday.name },
    };
  }
}

// Registered in admin.js
export { HolidayDatepickerController };
